import { memo, useEffect, useMemo, useState } from "react";
import QRCodeReact from "qrcode.react";
import { Agent } from '@/types/agent/agent';
import { QRCodeStyle, QRDisplayConfig, QRLabelOption } from '@/types/ui/branding';

interface BrandedQRCodeProps {
  value: string;
  size?: number;
  style?: QRCodeStyle;
  genie: Agent;
  includeMargin?: boolean;
  displayConfig?: QRDisplayConfig;
}

const getLabelText = (label: QRLabelOption, genieName: string): string | null => {
  switch (label) {
    case 'scan-to-connect':
      return 'Scan to Connect';
    case 'scan-to-chat':
      return 'Scan to Chat';
    case 'talk-to-genie':
      return `Talk to ${genieName}`;
    case 'none':
      return null;
  }
};

export const BrandedQRCode = memo(({
  value,
  size = 280,
  style,
  genie,
  includeMargin = false,
  displayConfig = {
    style: 'framed',
    label: 'scan-to-connect',
    transparentBackground: false,
    showLogo: true,
    foregroundColor: '#000000',
    backgroundColor: '#FFFFFF'
  }
}: BrandedQRCodeProps) => {
  const qrSize = size;

  const brandingConfig = useMemo(() => {
    const hasCustomBranding = !!(genie.branding?.logoUrl || (genie.branding?.primaryColor && genie.branding.primaryColor !== "#4E9CFF"));

    const primaryColor = hasCustomBranding ? genie.branding?.primaryColor || "#4E9CFF" : "#4E9CFF";
    const secondaryColor = hasCustomBranding ? genie.branding?.secondaryColor || primaryColor : "#3B82F6";

    return {
      hasCustomBranding,
      primaryColor,
      secondaryColor,
      gradientEnabled: genie.branding?.gradientEnabled ?? !hasCustomBranding,
      gradientAngle: genie.branding?.gradientAngle || 135,
    };
  }, [genie.branding]);

  const [coloredGenieUrl, setColoredGenieUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!brandingConfig.hasCustomBranding) {
      fetch("/genie.svg")
        .then((response) => response.text())
        .then((svgText) => {
          const coloredSvg = svgText.replace(/fill="#[0-9a-fA-F]{6}"/g, `fill="${brandingConfig.primaryColor}"`);
          const dataUrl = `data:image/svg+xml,${encodeURIComponent(coloredSvg)}`;
          setColoredGenieUrl(dataUrl);
        })
        .catch(() => {
          setColoredGenieUrl("/helpgenie_logo.png");
        });
    }
  }, [brandingConfig.hasCustomBranding, brandingConfig.primaryColor]);

  // Only show logo if showLogo is true in displayConfig
  const showLogo = displayConfig.showLogo ?? true;
  const logoUrl = showLogo
    ? (brandingConfig.hasCustomBranding ? genie.branding?.logoUrl : coloredGenieUrl)
    : null;

  // Use colors from displayConfig, falling back to style prop, then defaults
  const backgroundColor = displayConfig.backgroundColor || style?.backgroundColor || "#FFFFFF";
  const foregroundColor = displayConfig.foregroundColor || style?.foregroundColor || "#000000";

  const labelText = getLabelText(displayConfig.label, genie.name);

  // Render based on display style
  if (displayConfig.style === 'minimal') {
    return (
      <div
        className="branded-qr-container"
        style={{
          padding: "16px",
          background: displayConfig.transparentBackground ? 'transparent' : backgroundColor,
          borderRadius: "12px",
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <QRCodeReact
          value={value}
          size={qrSize}
          fgColor={foregroundColor}
          bgColor={displayConfig.transparentBackground ? 'transparent' : backgroundColor}
          includeMargin={includeMargin}
          level="H"
          imageSettings={
            logoUrl
              ? {
                  src: logoUrl,
                  height: qrSize * 0.15,
                  width: qrSize * 0.15,
                  excavate: true,
                }
              : undefined
          }
        />
        {labelText && (
          <p
            style={{
              marginTop: "12px",
              fontSize: "14px",
              fontWeight: "500",
              color: brandingConfig.primaryColor,
              fontFamily: "Poppins, system-ui, sans-serif",
            }}
          >
            {labelText}
          </p>
        )}
      </div>
    );
  }

  if (displayConfig.style === 'card') {
    return (
      <div
        className="branded-qr-container"
        style={{
          padding: "24px",
          background: displayConfig.transparentBackground ? 'transparent' : backgroundColor,
          borderRadius: "16px",
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: displayConfig.transparentBackground ? 'none' : "0 4px 20px rgba(0,0,0,0.1)",
          border: displayConfig.transparentBackground ? 'none' : "1px solid #e5e7eb",
        }}
      >
        <QRCodeReact
          value={value}
          size={qrSize}
          fgColor={foregroundColor}
          bgColor={displayConfig.transparentBackground ? 'transparent' : backgroundColor}
          includeMargin={includeMargin}
          level="H"
          imageSettings={
            logoUrl
              ? {
                  src: logoUrl,
                  height: qrSize * 0.15,
                  width: qrSize * 0.15,
                  excavate: true,
                }
              : undefined
          }
        />
        <div style={{ marginTop: "16px", textAlign: "center" }}>
          <p
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: "600",
              color: "#1f2937",
              fontFamily: "Poppins, system-ui, sans-serif",
            }}
          >
            {genie.name}
          </p>
          {labelText && (
            <p
              style={{
                margin: "4px 0 0 0",
                fontSize: "13px",
                fontWeight: "400",
                color: brandingConfig.primaryColor,
                fontFamily: "Poppins, system-ui, sans-serif",
              }}
            >
              {labelText}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (displayConfig.style === 'clean') {
    return (
      <div
        className="branded-qr-container"
        style={{
          padding: "24px",
          background: displayConfig.transparentBackground ? 'transparent' : backgroundColor,
          borderRadius: "16px",
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border: displayConfig.transparentBackground ? 'none' : `2px solid ${brandingConfig.primaryColor}20`,
        }}
      >
        <QRCodeReact
          value={value}
          size={qrSize}
          fgColor={foregroundColor}
          bgColor={displayConfig.transparentBackground ? 'transparent' : backgroundColor}
          includeMargin={includeMargin}
          level="H"
          imageSettings={
            logoUrl
              ? {
                  src: logoUrl,
                  height: qrSize * 0.15,
                  width: qrSize * 0.15,
                  excavate: true,
                }
              : undefined
          }
        />
        {labelText && (
          <p
            style={{
              marginTop: "16px",
              fontSize: "14px",
              fontWeight: "500",
              color: brandingConfig.primaryColor,
              fontFamily: "Poppins, system-ui, sans-serif",
            }}
          >
            {labelText}
          </p>
        )}
      </div>
    );
  }

  // Default: Framed style (original)
  const frameSize = size + 120;

  return (
    <div
      className="branded-qr-container"
      style={{
        width: frameSize,
        height: frameSize + (labelText ? 0 : -50),
        padding: "20px",
        background: displayConfig.transparentBackground
          ? 'transparent'
          : brandingConfig.gradientEnabled
            ? `linear-gradient(${brandingConfig.gradientAngle}deg, ${brandingConfig.primaryColor}, ${brandingConfig.secondaryColor})`
            : brandingConfig.primaryColor,
        borderRadius: "20px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        boxShadow: displayConfig.transparentBackground ? 'none' : "0 10px 30px rgba(0,0,0,0.15)",
      }}
    >
      {/* QR Code Container */}
      <div
        style={{
          background: backgroundColor,
          padding: "20px",
          borderRadius: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "inset 0 2px 10px rgba(0,0,0,0.1)",
          position: "relative",
        }}
      >
        {/* Decorative corners */}
        <svg
          style={{
            position: "absolute",
            top: "8px",
            left: "8px",
            width: "24px",
            height: "24px",
          }}
        >
          <path d="M0,12 L0,0 L12,0" stroke={brandingConfig.primaryColor} strokeWidth="3" fill="none" />
        </svg>
        <svg
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            width: "24px",
            height: "24px",
          }}
        >
          <path d="M12,0 L24,0 L24,12" stroke={brandingConfig.primaryColor} strokeWidth="3" fill="none" />
        </svg>
        <svg
          style={{
            position: "absolute",
            bottom: "8px",
            left: "8px",
            width: "24px",
            height: "24px",
          }}
        >
          <path d="M0,12 L0,24 L12,24" stroke={brandingConfig.primaryColor} strokeWidth="3" fill="none" />
        </svg>
        <svg
          style={{
            position: "absolute",
            bottom: "8px",
            right: "8px",
            width: "24px",
            height: "24px",
          }}
        >
          <path d="M12,24 L24,24 L24,12" stroke={brandingConfig.primaryColor} strokeWidth="3" fill="none" />
        </svg>

        {/* QR Code with logo - uses HIGH error correction to ensure scannability */}
        <QRCodeReact
          value={value}
          size={qrSize}
          fgColor={foregroundColor}
          bgColor={backgroundColor}
          includeMargin={includeMargin}
          level="H" // HIGH error correction (30% of data can be lost/covered)
          imageSettings={
            logoUrl
              ? {
                  src: logoUrl,
                  height: qrSize * 0.15, // Logo takes up 15% of QR code height
                  width: qrSize * 0.15, // Logo takes up 15% of QR code width
                  excavate: true, // Removes QR code dots behind the logo
                }
              : undefined
          }
        />
      </div>

      {/* Simple Call to Action */}
      {labelText && (
        <div
          style={{
            marginTop: "20px",
            padding: "8px 20px",
            background: "rgba(255, 255, 255, 0.95)",
            borderRadius: "20px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            display: "inline-block",
            whiteSpace: "nowrap",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              fontWeight: "500",
              color: brandingConfig.primaryColor,
              fontFamily: "Poppins, system-ui, sans-serif",
            }}
          >
            {labelText}
          </p>
        </div>
      )}
    </div>
  );
});
